import { observable, makeObservable } from 'mobx';
import { Axios } from '../utilities/network';
import { CURRENT_PLATFORM } from '../constants/settings';
import RootStore from './root-store';
import {
	XeroAccount, XeroAccountCodes, XeroAutoSyncSetting, XeroBankAccount,
	XeroConnectionStatus, XeroInvoice, XeroInvoiceSearch, XeroInvoiceSyncResult,
	XeroOrganisation,
} from '../utilities/xero';

// Every /api/Xero/* endpoint (Callback excluded — that's Xero's server calling
// OUR backend, never called from here) wraps its payload in
// `{ isSuccess, response }` instead of returning the data directly the way
// every other endpoint in this app does — see xero-frontend-spec.md §1.3.
// This one helper is the single place that unwraps it, so a store method body
// reads exactly like every other store's (`return Promise.resolve(res.data)`
// would silently return the envelope instead of the payload otherwise).
const XeroResponse = <T,>(res: any): T => res.data?.response as T;

export default class XeroStore {

	rootStore: RootStore;

	connecting: boolean = false;
	loadingConnection: boolean = false;
	disconnecting: boolean = false;
	loadingOrganisations: boolean = false;
	switchingOrganisation: boolean = false;
	loadingAccounts: boolean = false;
	savingAccountCodes: boolean = false;
	loadingBankAccounts: boolean = false;
	loadingAutoSync: boolean = false;
	savingAutoSync: boolean = false;
	pushingInvoice: boolean = false;
	loadingSyncResult: boolean = false;
	searchingInvoices: boolean = false;

	constructor(rootStore: RootStore) {
		makeObservable(this, {
			connecting: observable,
			loadingConnection: observable,
			disconnecting: observable,
			loadingOrganisations: observable,
			switchingOrganisation: observable,
			loadingAccounts: observable,
			savingAccountCodes: observable,
			loadingBankAccounts: observable,
			loadingAutoSync: observable,
			savingAutoSync: observable,
			pushingInvoice: observable,
			loadingSyncResult: observable,
			searchingInvoices: observable,
		});
		this.rootStore = rootStore;
	}

	// Returns the Xero OAuth consent URL — the caller MUST do a full-page
	// redirect (`window.location.href = url`), never an iframe/popup: Xero's
	// authorisation page sets frame-busting headers and popups get blocked.
	startConnect = async (): Promise<string> => {
		this.connecting = true;
		try {
			const res = await Axios.post(`/api/Xero/Connect?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(XeroResponse<string>(res));
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.connecting = false;
		}
	};

	getConnection = async (): Promise<XeroConnectionStatus> => {
		this.loadingConnection = true;
		try {
			const res = await Axios.get(`/api/Xero/Connection?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(XeroResponse<XeroConnectionStatus>(res));
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingConnection = false;
		}
	};

	disconnect = async (): Promise<string> => {
		this.disconnecting = true;
		try {
			const res = await Axios.delete(`/api/Xero/Connection?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(XeroResponse<string>(res));
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.disconnecting = false;
		}
	};

	// All organisations this Xero authorisation covers — the backend only
	// ever stores/uses ONE of these (XeroConnectionStatus.tenantId), defaulting
	// to whichever one the OAuth response listed first. >1 result means that
	// pick was arbitrary from the practitioner's point of view, so the caller
	// MUST let them confirm/change it rather than silently trusting it — see
	// xero-frontend-spec.md §4.5.
	getOrganisations = async (): Promise<XeroOrganisation[]> => {
		this.loadingOrganisations = true;
		try {
			const res = await Axios.get(`/api/Xero/Organisations?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(XeroResponse<XeroOrganisation[]>(res) ?? []);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingOrganisations = false;
		}
	};

	// Doesn't re-run the OAuth consent — the same token already covers every
	// organisation the practitioner authorised, this just changes which one
	// subsequent calls (Accounts/BankAccounts/AutoSync/Invoices/...) target.
	// The caller MUST have already confirmed the wipe (account codes, bank
	// account, auto-sync all reset — they're specific to the old organisation)
	// before calling this, and MUST reload connection/accounts and send the
	// practitioner back to account mapping afterward.
	selectOrganisation = async (tenantId: string): Promise<XeroOrganisation> => {
		this.switchingOrganisation = true;
		try {
			const res = await Axios.put(`/api/Xero/Organisation?platformId=${CURRENT_PLATFORM}`, { tenantId });
			return Promise.resolve(XeroResponse<XeroOrganisation>(res));
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.switchingOrganisation = false;
		}
	};

	getAccounts = async (): Promise<XeroAccount[]> => {
		this.loadingAccounts = true;
		try {
			const res = await Axios.get(`/api/Xero/Accounts?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(XeroResponse<XeroAccount[]>(res) ?? []);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingAccounts = false;
		}
	};

	setAccountCodes = async (reqModel: XeroAccountCodes): Promise<XeroAccountCodes> => {
		this.savingAccountCodes = true;
		try {
			const res = await Axios.put(`/api/Xero/AccountCodes?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(XeroResponse<XeroAccountCodes>(res));
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.savingAccountCodes = false;
		}
	};

	getBankAccounts = async (): Promise<XeroBankAccount[]> => {
		this.loadingBankAccounts = true;
		try {
			const res = await Axios.get(`/api/Xero/BankAccounts?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(XeroResponse<XeroBankAccount[]>(res) ?? []);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingBankAccounts = false;
		}
	};

	getAutoSync = async (): Promise<XeroAutoSyncSetting> => {
		this.loadingAutoSync = true;
		try {
			const res = await Axios.get(`/api/Xero/AutoSync?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(XeroResponse<XeroAutoSyncSetting>(res));
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingAutoSync = false;
		}
	};

	setAutoSync = async (reqModel: XeroAutoSyncSetting): Promise<XeroAutoSyncSetting> => {
		this.savingAutoSync = true;
		try {
			const res = await Axios.put(`/api/Xero/AutoSync?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(XeroResponse<XeroAutoSyncSetting>(res));
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.savingAutoSync = false;
		}
	};

	// Safe to call repeatedly — the backend guarantees it never double-creates
	// an invoice (already-synced records come back with alreadySynced: true;
	// a call that previously timed out but actually succeeded on Xero's side
	// gets claimed by reference lookup instead of creating a duplicate). No
	// client-side de-dupe beyond the usual "don't let the button double-fire"
	// is needed.
	pushInvoice = async (patientHistoryId: number): Promise<XeroInvoiceSyncResult> => {
		this.pushingInvoice = true;
		try {
			const res = await Axios.post(`/api/Xero/Invoices/Sync/${patientHistoryId}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(XeroResponse<XeroInvoiceSyncResult>(res));
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.pushingInvoice = false;
		}
	};

	getSyncResult = async (patientHistoryId: number): Promise<XeroInvoiceSyncResult | null> => {
		this.loadingSyncResult = true;
		try {
			const res = await Axios.get(`/api/Xero/Invoices/Sync/${patientHistoryId}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(XeroResponse<XeroInvoiceSyncResult | null>(res));
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingSyncResult = false;
		}
	};

	searchInvoices = async (take: number, reqModel: XeroInvoiceSearch): Promise<XeroInvoice[]> => {
		this.searchingInvoices = true;
		try {
			const res = await Axios.post(`/api/Xero/Invoices/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(XeroResponse<XeroInvoice[]>(res) ?? []);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.searchingInvoices = false;
		}
	};
}
